import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode
} from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { fetchIsSudo } from "./utils";
import { SudoEventEmitter, SudoEvents } from "./events";

export enum SudoRoutes {
  RequestOTP = "/sudo/request-otp",
  SubmitOTP = "/sudo/submit-otp"
}

type OnEstablishedSudoType = null | (() => Promise<void>);

interface SudoContextType {
  originalRoute: string | null;
  destinationRoute: string | null;
  setDestinationRoute: (route: string) => void;
  setOriginalRoute: (route: string) => void;
  formData: any;
  setFormData: (data: any) => void;
  onFormDataChange: (update: any) => void;
  closeRouteOverride: string | null;
  setCloseRouteOverride: (route: string | null) => void;
  establishSudo: (
    destinationRoute: string,
    onEstablishedSudoCb?: OnEstablishedSudoType
  ) => Promise<void>;
  setOnCancel: (onCancel: () => void) => void;
  onCancel?: () => void;
  onEstablishedSudo: OnEstablishedSudoType;
  cancelSudo: () => void;
  onSetInitialSteps: (steps: number) => void;
  totalSteps: number | null;
  requestOtpRoute: string;
  submitOtpRoute: string;
}

const defaultSudoContext: SudoContextType = {
  originalRoute: null,
  destinationRoute: null,
  setDestinationRoute: () => {},
  setOriginalRoute: () => {},
  setOnCancel: () => {},
  onCancel: () => {},
  formData: null,
  setFormData: () => {},
  onFormDataChange: () => {},
  closeRouteOverride: null,
  setCloseRouteOverride: () => {},
  establishSudo: async () => {},
  onEstablishedSudo: () => Promise.resolve(),
  cancelSudo: () => {},
  onSetInitialSteps: () => {},
  totalSteps: null,
  requestOtpRoute: "",
  submitOtpRoute: ""
};

const SudoContext = createContext<SudoContextType>(defaultSudoContext);

export const useSudoContext = () => useContext(SudoContext);

interface SudoProviderProps {
  children: ReactNode;
  basename?: string;
}

export const SudoProvider = ({
  children,
  basename = "/"
}: SudoProviderProps) => {
  const [onEstablishedSudo, setOnEstablishedSudo] =
    useState<OnEstablishedSudoType>(null);
  const [onCancel, setOnCancel] = useState<() => void>();
  const [originalRoute, setOriginalRoute] = useState<
    SudoContextType["originalRoute"]
  >(defaultSudoContext.originalRoute);
  const [destinationRoute, setDestinationRoute] = useState<
    SudoContextType["destinationRoute"]
  >(defaultSudoContext.destinationRoute);
  const [formData, setFormData] = useState<SudoContextType["formData"]>(
    defaultSudoContext.formData
  );
  const [closeRouteOverride, setCloseRouteOverride] = useState<string | null>(
    defaultSudoContext.closeRouteOverride
  );
  const [initialSteps, setInitialSteps] = useState<number | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const baseRoute =
    basename === "/" ? "" : `${basename.match(/^\//) ? "" : "/"}${basename}`;
  const requestOtpRoute = `${baseRoute}${SudoRoutes.RequestOTP}`;
  const submitOtpRoute = `${baseRoute}${SudoRoutes.SubmitOTP}`;

  const onSetInitialSteps = useCallback((steps: number) => {
    setInitialSteps(steps);
  }, []);

  useEffect(() => {
    const hasDefaultChanged =
      originalRoute !== defaultSudoContext.originalRoute ||
      destinationRoute !== defaultSudoContext.destinationRoute ||
      formData !== defaultSudoContext.formData;

    if (
      ![
        requestOtpRoute,
        submitOtpRoute,
        originalRoute,
        destinationRoute
      ].includes(location.pathname) &&
      hasDefaultChanged
    ) {
      setFormData(defaultSudoContext.formData);
      setDestinationRoute(defaultSudoContext.destinationRoute);
      setOriginalRoute(defaultSudoContext.originalRoute);
      setOnEstablishedSudo(defaultSudoContext.onEstablishedSudo);
    }
  }, [location.pathname]);

  const establishSudo = useCallback(
    (
      destRoute: string,
      onEstablishedSudoCb?: OnEstablishedSudoType
    ): Promise<void> => {
      if (!originalRoute) {
        setOriginalRoute(location.pathname);
      }
      setDestinationRoute(destRoute);
      setOnEstablishedSudo(() => onEstablishedSudoCb || null);

      return fetchIsSudo()
        .then(async () => {
          // If already in sudo mode, execute callback and navigate.
          setCloseRouteOverride(null);
          await (onEstablishedSudoCb
            ? onEstablishedSudoCb?.()
            : Promise.resolve());
          navigate(destRoute);
        })
        .catch(
          () =>
            // Not in sudo mode, need to establish sudo
            // Return a promise that resolves when sudo is established
            new Promise<void>((resolve, reject) => {
              let unsubscribeCancelled: () => void;

              // Set up event listeners
              const unsubscribeEstablished = SudoEventEmitter.subscribe(
                SudoEvents.ESTABLISHED,
                async () => {
                  try {
                    // Execute the callback if provided
                    await (onEstablishedSudoCb
                      ? onEstablishedSudoCb?.()
                      : Promise.resolve());

                    setCloseRouteOverride(null);
                    // Navigate to destination
                    navigate(destRoute);

                    // Clean up listeners
                    unsubscribeEstablished();
                    unsubscribeCancelled();

                    resolve();
                  } catch (callbackError) {
                    setCloseRouteOverride(null);
                    // Clean up listeners
                    unsubscribeEstablished();
                    unsubscribeCancelled();
                    reject(callbackError);
                  }
                }
              );

              unsubscribeCancelled = SudoEventEmitter.subscribe(
                SudoEvents.CANCELLED,
                () => {
                  setCloseRouteOverride(null);

                  // Clean up listeners
                  unsubscribeEstablished();
                  unsubscribeCancelled();
                  reject(new Error("Sudo flow was cancelled"));
                }
              );

              // Navigate to sudo prompt
              navigate(requestOtpRoute);
            })
        );
    },
    [location, navigate, originalRoute, requestOtpRoute]
  );

  const cancelSudo = useCallback(() => {
    SudoEventEmitter.emit(SudoEvents.CANCELLED);
    setCloseRouteOverride(null);
    onCancel?.();
    navigate(closeRouteOverride || originalRoute || baseRoute);
  }, [
    navigate,
    closeRouteOverride,
    originalRoute,
    baseRoute,
    onCancel,
    setCloseRouteOverride
  ]);

  const onFormDataChange = useCallback((update: any) => {
    setFormData((prev: any) => ({ ...prev, ...update }));
  }, []);

  return (
    <SudoContext.Provider
      value={{
        originalRoute,
        destinationRoute,
        setDestinationRoute,
        setOriginalRoute,
        formData,
        setFormData,
        onFormDataChange,
        closeRouteOverride,
        setCloseRouteOverride,
        establishSudo,
        onEstablishedSudo,
        cancelSudo,
        setOnCancel,
        onCancel,
        onSetInitialSteps,
        totalSteps: initialSteps,
        requestOtpRoute,
        submitOtpRoute
      }}
    >
      {children}
    </SudoContext.Provider>
  );
};
